import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type CreateSectionRequest,
  type Section,
  type UpdateSectionRequest,
} from "@eduviaos/shared";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { useCreateSection, useUpdateSection } from "@/hooks/useAcademicCatalogue";
import { draftRow, useAIDraftGenerator, type SectionDraft } from "@/lib/aiDraft";

/**
 * Phase H4 FE-1 — section editor sheet.
 *
 * Sections are scoped by `(branch_id, grade_ref, academic_year_id,
 * name)` and the BE enforces uniqueness on that tuple. The create
 * form requires those four fields; edit mode keeps the scope
 * read-only (re-binding a section would scramble class-teacher
 * assignments and roster history) and only exposes capacity +
 * display ordering for change.
 *
 * Rationale for free-text grade_ref and academic_year_id:
 *   - The AcademicGradeConfig + AcademicYearConfig capabilities land
 *     in a later phase. Until then the BE accepts free-text and the
 *     readiness aggregator counts whatever the admin enters. We
 *     surface a hint inline so admins use stable strings (e.g.
 *     "9-A" not "9 A" / "9A" interchangeably).
 */

interface DraftState {
  branch_id: string;
  grade_ref: string;
  name: string;
  academic_year_id: string;
  display_order: number;
  max_students: string;
  default_room_id: string;
}

const EMPTY_DRAFT: DraftState = {
  branch_id: "",
  grade_ref: "",
  name: "",
  academic_year_id: "",
  display_order: 0,
  max_students: "",
  default_room_id: "",
};

function describeSectionDraft(draft: SectionDraft) {
  return [
    ...draftRow("Grade key", draft.grade_ref),
    ...draftRow("Section name", draft.name),
    ...draftRow("Capacity", draft.max_students),
    ...draftRow("Display order", draft.display_order),
  ];
}

function mergeSectionDraft(prev: DraftState, draft: SectionDraft): DraftState {
  return {
    ...prev,
    grade_ref: draft.grade_ref ?? prev.grade_ref,
    name: draft.name ?? prev.name,
    max_students: draft.max_students === null ? prev.max_students : String(draft.max_students),
    display_order: draft.display_order ?? prev.display_order,
  };
}

function fromExisting(section: Section): DraftState {
  return {
    branch_id: section.branch_id,
    grade_ref: section.grade_ref,
    name: section.name,
    academic_year_id: section.academic_year_id,
    display_order: section.display_order,
    max_students: section.max_students == null ? "" : String(section.max_students),
    default_room_id: section.default_room_id ?? "",
  };
}

interface SectionEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: Section | null;
  /** Default branch / year for new rows (the page's filter scope so a
   * "New section" click doesn't dump the admin into an empty form). */
  defaultBranchId?: string | null;
  defaultAcademicYearId?: string | null;
  onSaved?: (saved: Section) => void;
}

export function SectionEditorSheet({
  open,
  onOpenChange,
  existing,
  defaultBranchId = null,
  defaultAcademicYearId = null,
  onSaved,
}: SectionEditorSheetProps) {
  const [draft, setDraft] = React.useState<DraftState>(EMPTY_DRAFT);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const createMutation = useCreateSection();
  const updateMutation = useUpdateSection();

  const isEdit = existing !== null;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const generateSectionDraft = useAIDraftGenerator("section");

  React.useEffect(() => {
    if (!open) return;
    if (existing) {
      setDraft(fromExisting(existing));
    } else {
      setDraft({
        ...EMPTY_DRAFT,
        branch_id: defaultBranchId ?? "",
        academic_year_id: defaultAcademicYearId ?? "",
      });
    }
    setErrorMessage(null);
  }, [open, existing, defaultBranchId, defaultAcademicYearId]);

  const handleSave = async () => {
    setErrorMessage(null);
    if (!isEdit) {
      if (!draft.branch_id.trim()) {
        setErrorMessage("Branch is required.");
        return;
      }
      if (!draft.grade_ref.trim()) {
        setErrorMessage("Grade key is required.");
        return;
      }
      if (!draft.academic_year_id.trim()) {
        setErrorMessage("Academic year is required.");
        return;
      }
    }
    const name = draft.name.trim();
    if (!name) {
      setErrorMessage("Section name is required.");
      return;
    }

    const maxStudents =
      draft.max_students.trim() === "" ? null : Number.parseInt(draft.max_students, 10);
    if (maxStudents != null && (Number.isNaN(maxStudents) || maxStudents < 0)) {
      setErrorMessage("Max students must be a non-negative number, or leave it blank for no cap.");
      return;
    }

    try {
      if (isEdit && existing) {
        const body: UpdateSectionRequest = {
          display_order: draft.display_order,
          max_students: maxStudents,
          default_room_id: draft.default_room_id.trim() || null,
        };
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        onSaved?.(saved);
        toast.success(`Saved changes to ${saved.grade_ref}-${saved.name}.`);
        onOpenChange(false);
      } else {
        const body: CreateSectionRequest = {
          branch_id: draft.branch_id.trim(),
          grade_ref: draft.grade_ref.trim(),
          name,
          academic_year_id: draft.academic_year_id.trim(),
          display_order: draft.display_order,
          max_students: maxStudents,
          default_room_id: draft.default_room_id.trim() || null,
        };
        const saved = await createMutation.mutateAsync(body);
        onSaved?.(saved);
        toast.success(`Created section ${saved.grade_ref}-${saved.name}.`);
        onOpenChange(false);
      }
    } catch (raw) {
      if (raw instanceof ApiError) {
        if (raw.code === "CONFLICT") {
          setErrorMessage(
            `A section named "${name}" already exists for grade "${draft.grade_ref}" in this branch + academic year.`,
          );
          return;
        }
        if (raw.message) {
          setErrorMessage(raw.message);
          return;
        }
      }
      setErrorMessage(
        raw instanceof Error && raw.message
          ? raw.message
          : "Could not save the section — please try again.",
      );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full max-w-lg flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="border-border/60 border-b px-6 py-4">
          <SheetTitle>
            {isEdit ? `Edit section: ${existing?.grade_ref}-${existing?.name}` : "New section"}
          </SheetTitle>
          <SheetDescription>
            Sections are scoped to a branch + grade + academic year. The combination of those three
            plus the section name must be unique within your institution.
          </SheetDescription>
          {isEdit ? null : (
            <div className="pt-2">
              <AIDraftButton<SectionDraft>
                title="Draft a section"
                placeholder="e.g. Grade 9 section B with room for 40"
                generate={generateSectionDraft}
                describe={describeSectionDraft}
                onApply={(next) => {
                  setDraft((prev) => mergeSectionDraft(prev, next));
                  setErrorMessage(null);
                }}
                disabled={isPending}
                data-testid="section-ai-draft"
              />
            </div>
          )}
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          {errorMessage ? (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">{errorMessage}</span>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="section-branch">Branch ID</Label>
            <Input
              id="section-branch"
              value={draft.branch_id}
              onChange={(event) => setDraft((prev) => ({ ...prev, branch_id: event.target.value }))}
              disabled={isEdit}
              placeholder="hq"
            />
            <p className="text-muted-foreground text-[11px] leading-snug">
              {isEdit
                ? "Branch is immutable after creation — to move a section, archive it and create a new one in the target branch."
                : "Branch slug or ID. Use the Branches page to confirm."}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="section-grade">Grade key</Label>
              <Input
                id="section-grade"
                value={draft.grade_ref}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, grade_ref: event.target.value }))
                }
                disabled={isEdit}
                placeholder="9"
                maxLength={24}
              />
              <p className="text-muted-foreground text-[11px] leading-snug">
                Free-text today (capability config lands in a later phase). Use a stable scheme like{" "}
                <code className="text-foreground">9</code> or{" "}
                <code className="text-foreground">grade_9</code>.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="section-name">Section name</Label>
              <Input
                id="section-name"
                value={draft.name}
                onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="A"
                maxLength={40}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="section-year">Academic year</Label>
            <Input
              id="section-year"
              value={draft.academic_year_id}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, academic_year_id: event.target.value }))
              }
              disabled={isEdit}
              placeholder="ay-2026-27"
            />
            <p className="text-muted-foreground text-[11px] leading-snug">
              {isEdit
                ? "Academic year is immutable — sections roll over to the next year via a separate copy flow (Phase G3+)."
                : "Stable year identifier. Use the same scheme across all sections so the timetable solver can fan out."}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="section-order">Display order</Label>
              <Input
                id="section-order"
                type="number"
                min={0}
                max={999}
                value={draft.display_order}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    display_order: clampInt(event.target.value, 0, 999, 0),
                  }))
                }
              />
              <p className="text-muted-foreground text-[11px] leading-snug">
                Lower values sort first in pickers. Tied values fall back to alphabetical.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="section-max">Max students</Label>
              <Input
                id="section-max"
                type="number"
                min={0}
                max={10000}
                value={draft.max_students}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, max_students: event.target.value }))
                }
                placeholder="No cap"
              />
              <p className="text-muted-foreground text-[11px] leading-snug">
                Optional. Enrolment refuses to exceed the cap once Phase G7 enrolment hooks ship.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="section-room">Default room</Label>
            <Input
              id="section-room"
              value={draft.default_room_id}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, default_room_id: event.target.value }))
              }
              placeholder="Room 204"
            />
            <p className="text-muted-foreground text-[11px] leading-snug">
              Slot-level overrides take precedence over this default. Leave blank if rooms aren't
              tracked yet.
            </p>
          </div>
        </div>

        <div className="border-border/60 flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                Saving…
              </>
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Create section"
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}
